import {getColors, DEFAULT_TRIP_COLOR} from 'functions/general/color_functions.js'
import { isFiniteNumeric } from 'functions/numbers';


/**
 * Convert a hex color string ('#rrggbb' or '#rrggbbaa') into a 24-bit
 * integer 0xRRGGBB. Used by Mapbox cluster aggregations so we can detect
 * when every leaf in a cluster shares the same color (min === max).
 * Returns -1 for invalid input so it can never coincidentally equal a
 * valid color when only some leaves are missing colors.
 */
export function hexColorToInt(hex) {
  if (!hex || typeof hex !== 'string') return -1;
  const s = hex.replace('#', '').slice(0, 6);
  if (s.length < 6) return -1;
  const n = parseInt(s, 16);
  return Number.isFinite(n) ? n : -1;
}


const DROPPED_POINT_COLOR = '#7a0202ff';
const DROPPED_TRIP_COLOR = '#cf0000ff';
const DROPPED_TRIP_POINT_COLOR = '#7a0202ff';


//https://stackoverflow.com/a/8809472/11342048
export function generateUUID() { // Public Domain/MIT
  let d = new Date().getTime();//Timestamp
  let d2 = ((typeof performance !== 'undefined') && performance.now && (performance.now()*1000)) || 0;//Time in microseconds since page-load or 0 if unsupported
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      let r = Math.random() * 16;//random number between 0 and 16
      if(d > 0){//Use timestamp until depleted
          r = (d + r)%16 | 0;
          d = Math.floor(d/16);
      } else {//Use microseconds since page-load if supported
          r = (d2 + r)%16 | 0;
          d2 = Math.floor(d2/16);
      }
      return (c === 'x' ? r : ((r & 0x3) | 0x8)).toString(16);
  });
}




export default function createPointGeoJson({locationName, latitude, longitude, uuid, name, color=null, 
  dataRow=null, isResult=false, type_='point', isDrop=false}) {
  
  if (!uuid) {
    const err_msg = 'Expecting an input uuid in createPointGeoJson in the geoJsonFunctions.js script'
    throw err_msg;
  }

  const colors = getColors(color);

  const geoJson = {          
      type: 'Feature', 
      properties: {
        'marker-color': '#ff0000', 
        "fill": "#66ff66", 
        "marker-size": "small",
        name: name || locationName,
        color: colors.rgbColor,
        defaultColor: colors.rgbColor,
        colorHover: colors.rgbColorHover,
        colorHex: colors.hexColor,
        colorRgb: colors.rgbColor,
        colorHoverHex: colors.hexColorHover,
        colorHoverRgb: colors.rgbColorHover,
        colorInt: hexColorToInt(colors.hexColor),
        uuid: uuid,
        type: type_,
        type_: type_,
        isResult: isResult,
        isDrop: isDrop
      },
      geometry: {
          type: 'Point',
          coordinates: [parseFloat(longitude), parseFloat(latitude)]
      }
  }

  dataRow && Object.keys(dataRow).forEach((key) => {
    geoJson.properties[key] = dataRow[key];
  })

  return geoJson
}

/**
 * Build a lookup of location rows keyed by their ids (locationId and UUID),
 * used to resolve coordinates for backend result rows that arrive without
 * them. Cartesian models submit locations with null lat/lng (the solver only
 * needs the travel-cost matrix), so their echoed paths/drops carry no
 * coordinates — the frontend re-attaches them from its own locations table.
 * Also hardens the geographic case against sparse backend echoes.
 */
function buildLocationLookup(locations) {
  const byId = new Map();
  for (const row of locations || []) {
    if (row?.locationId != null) byId.set(String(row.locationId), row);
    if (row?.UUID != null) byId.set(String(row.UUID), row);
  }
  return byId;
}

// Shared guard: a cell of only spaces used to pass this and read as 0.
const isCoord = (value) => isFiniteNumeric(value);

/** Resolve a lat/lng pair, falling back to the location row for `locId`. */
function resolveCoords(lat, lng, locId, locationsById) {
  if (isCoord(lat) && isCoord(lng)) return { lat, lng };
  const loc = locId != null && locationsById?.get(String(locId));
  if (loc && isCoord(loc.latitude) && isCoord(loc.longitude)) {
    return { lat: loc.latitude, lng: loc.longitude };
  }
  return { lat, lng };
}

export function createDropsGeoJson({drops, locationsById=null}) {
  const retGeoJson = [];
  drops.forEach((elem) => {
    if (elem['tripId']) {
      const from = resolveCoords(
        elem['From Latitude'] || elem['fromLatitude'],
        elem['From Longitude'] || elem['fromLongitude'],
        elem['fromLocationId'], locationsById);
      const to = resolveCoords(
        elem['To Latitude'] || elem['toLatitude'],
        elem['To Longitude'] || elem['toLongitude'],
        elem['toLocationId'], locationsById);
      const dropTrip = createTripGeoJson({
        fromLat: from.lat,
        fromLng: from.lng,
        toLat: to.lat,
        toLng: to.lng,
        fromName: elem['fromLocationName'],
        toName: elem['toLocationName'],
        uuid: elem['UUID'] || generateUUID(),
        fromUuid: elem['fromLocationId'],
        toUuid: elem['toLocationId'],
        vehicleId: null,
        color: DROPPED_TRIP_COLOR,
        type_: 'trip',
        dataRow: elem,
        isDrop: true,
        name: "Dropped Trip from " + elem['fromLocationName'] + " to " + elem['toLocationName']
      })
      retGeoJson.push(dropTrip.geoJson);

      const dropStart = createPointGeoJson({
        locationName: elem['fromLocationName'],
        latitude: from.lat,
        longitude: from.lng,
        uuid: elem['fromLocationId'] || elem['UUID'],
        isDrop: true,
        dataRow: elem,
        type_: 'trip_start',
        color: DROPPED_TRIP_POINT_COLOR,
        name: elem['fromLocationName']
      })
      retGeoJson.push(dropStart);

      const dropEnd = createPointGeoJson({
        locationName: elem['toLocationName'],
        latitude: to.lat,
        longitude: to.lng,
        uuid: elem['toLocationId'] || elem['UUID'],
        isDrop: true,
        dataRow: elem,
        type_: 'trip_end',
        color: DROPPED_TRIP_POINT_COLOR,
        name: elem['toLocationName']
      })
      retGeoJson.push(dropEnd);
    } else {
    const dropCoords = resolveCoords(elem['latitude'], elem['longitude'], elem['locationId'], locationsById);
    const dropPoint = createPointGeoJson({
      locationName: elem['locationName'],
      latitude: dropCoords.lat,
      longitude: dropCoords.lng,
      uuid: elem['locationId'] || elem['UUID'],
      isDrop: true,
      dataRow: elem,
      type_: 'location',
      color: DROPPED_POINT_COLOR
    })
    retGeoJson.push(dropPoint);
    }
  })
  return retGeoJson;
}

//showRetours - show a line string where from and to locations are the same
export function createRoutesGeoJson({vehiclePaths, showRetours=false, locationsById=null}) {
  const retGeoJson = [];
  vehiclePaths.forEach((elem) => {
    const vehicleId = elem['vehicle_id']
    // Runs in a reducer while applying solve results: a throw here would leave the
    // whole results layer unrendered, not just this vehicle's. Tolerate a path that
    // arrives already-parsed, and skip (loudly) one that can't be read at all.
    let pathJson;
    if (Array.isArray(elem['path'])) {
      pathJson = elem['path'];
    } else {
      try {
        pathJson = JSON.parse(elem['path']);
      } catch {
        console.error('Could not parse the route path for vehicle', vehicleId);
        return;
      }
    }
    if (!Array.isArray(pathJson)) return;
    pathJson.forEach((path, path_idx) => {
      // Backend legs echo the coordinates we submitted; cartesian models
      // submit null coords, so fall back to the local locations table.
      const from = resolveCoords(path['fromLatitude'], path['fromLongitude'], path['fromLocationId'], locationsById);
      const to = resolveCoords(path['toLatitude'], path['toLongitude'], path['toLocationId'], locationsById);
      const fromLat = from.lat;
      const fromLng = from.lng;
      const toLat = to.lat;
      const toLng = to.lng;
      const fromName = path['fromLocationName'];
      const toName = path['toLocationName'];
      const color = path['color']
      // createTripGeoJson throws on a falsy uuid, which would unrender the whole
      // results layer over one malformed leg. Fall back to a generated id, the
      // same defence createDropsGeoJson already uses for dropped-trip legs.
      const uuid = path['UUID'] || generateUUID();
      const fromUuid = path['fromLocationId'];
      const toUuid = path['toLocationId'];
      const locationColor = path['fromLocationColor'] || path['color'] || getColors().rgbColor;
      // The stop node is colored by its location; expose the vehicle's own color
      // separately so result cards can show which vehicle performs each visit.
      const vehicleColorHex = getColors(color).hexColor;
      let action;
      if (path_idx === 0) {
        action = path['From Action'];
      } else {
        // A leg missing its action list would make these spreads throw
        // (`...undefined`), and — like the parse failure above — a throw here
        // unrenders the whole results layer, not just this leg. Default to [].
        action = [...(pathJson[path_idx-1]['To Action'] || []), ...(path['From Action'] || [])]
      }
      if (showRetours || (fromUuid !== toUuid)) {
        const tripResults = createTripGeoJson({
          fromLat:fromLat, 
          fromLng: fromLng, 
          toLat: toLat,
          toLng: toLng,
          fromName: fromName,
          toName: toName,
          uuid: uuid,
          fromUuid: fromUuid,
          toUuid: toUuid,
          vehicleId: vehicleId,
          color: color,
          type_: 'routeLeg',
          dataRow: path,
          isResult: true
        })

        if (tripResults.valid) {
          retGeoJson.push(tripResults.geoJson)
        }
      }
      const geoJsonPoint = createPointGeoJson({
        locationName: fromName,
        latitude: fromLat,
        longitude: fromLng,
        global: true,
        color: locationColor,
        type_: 'routePoint',
        uuid: generateUUID(),
        isResult: true,
        dataRow: {...path, 'Stop': path_idx, 'vehicleId': vehicleId, 'Action': action, 'vehicleColorHex': vehicleColorHex}
      })

      retGeoJson.push(geoJsonPoint)

      if (path_idx === pathJson.length - 1) {
        if (fromName !== toName) {
          const geoJsonPoint = createPointGeoJson({
            locationName: toName,
            latitude: toLat,
            longitude: toLng,
            color: color,
            type_: 'routePoint',
            uuid: generateUUID(),
            isResult: true,
            dataRow: {...pathJson[path_idx+1], 'Stop': path_idx+1, 'vehicleId': vehicleId, 'Action': path['To Action'], 'vehicleColorHex': vehicleColorHex}
          })
          retGeoJson.push(geoJsonPoint)
        }
      }
    });
  });

  return retGeoJson;
}

export function createTripGeoJson({fromLat, fromLng, toLat, toLng, fromName, toName, uuid, fromUuid, toUuid, type_='trip',
  tripType='trip', vehicleId=null, color=null, isResult=false, dataRow={}, isDrop=false}) {

  if (!uuid) {
    const err_msg = 'Expecting an input uuid in createTripGeoJson in the geoJsonFunctions.js script'
    throw err_msg;
  }

  // A coordinate of 0 is valid — the old `!fromLat | !fromLng` check treated
  // it as missing (and used bitwise-or), which silently dropped any trip
  // touching the origin. That's an edge case geographically (null island) but
  // an ordinary one on an x-y grid.
  const valid = isCoord(fromLat) && isCoord(fromLng) && isCoord(toLat) && isCoord(toLng);

  // Uncolored trips fall back to the brand trip color rather than the
  // location default, which is what made every input trip render black.
  // Result legs and dropped trips always pass an explicit color.
  const colors = getColors(color, DEFAULT_TRIP_COLOR);
  let geoJson
  if (valid) {
    geoJson = {
      type: "Feature",
      properties: {
        toLoc: toName,
        fromLoc: fromName,
        toLat: toLat,
        toLng: toLng,
        fromLat: fromLat,
        fromLng: fromLng,
        name: fromName + ' to ' + toName,
        vehicleId: vehicleId,
        color: colors.rgbColor,
        defaultColor: colors.rgbColor,
        colorHover: colors.rgbColorHover,
        colorHex: colors.hexColor,
        colorRgb: colors.rgbColor,
        colorHoverHex: colors.hexColorHover,
        colorHoverRgb: colors.rgbColorHover,
        uuid: uuid,
        toUuid: toUuid,
        fromUuid: fromUuid,
        tripType: tripType,
        type: type_,
        isResult: isResult,
        isDrop: isDrop
      },
      geometry:
        {
          type: "LineString",
          coordinates: [[fromLng, fromLat],[toLng, toLat]]
        }
      }
    }
  else {
    geoJson = {
      type: "Feature",
      properties: {
        toLoc: toName,
        fromLoc: fromName,
        toLat: toLat,
        toLng: toLng,
        fromLat: fromLat,
        fromLng: fromLng,
        color: colors.rgbColor,
        defaultColor: colors.rgbColor,
        colorHover: colors.rgbColorHover,
        colorHex: colors.hexColor,
        colorRgb: colors.rgbColor,
        colorHoverHex: colors.hexColorHover,
        colorHoverRgb: colors.rgbColorHover,
        name: fromName + ' to ' + toName,
        uuid: uuid || generateUUID(),
        tripType: tripType,
        type: type_,
        isResult: isResult,
        isDrop: isDrop
      },
      geometry:
        {
          type: "LineString",
          coordinates: [[fromLng, fromLat],[toLng, toLat]]
        }
    }
  }    
  Object.keys(dataRow).forEach((key) => {
    geoJson.properties[key] = dataRow[key]
  })

  return {geoJson:geoJson, valid:valid}
}


export function updateGeoJsonFromUUID(geoJson, uuid, newField, newValue) {
  for (const feature of geoJson['features']) {
    if (feature.properties.uuid === uuid) {
      if (newField === 'name') {
        feature.properties.name = newValue;
      }
      else if (newField === 'lat') {
        feature.geometry.coordinates = [feature.geometry.coordinates[0], newValue]
      }
      else if (newField === 'lng') {
        feature.geometry.coordinates = [newValue, feature.geometry.coordinates[1]]
      }
      else if (newField === 'fromCoords') {
        feature.geometry.coordinates = [newValue, feature.geometry.coordinates[1]];
      }
      else if (newField === 'toCoords') {
        feature.geometry.coordinates = [feature.geometry.coordinates[0], newValue];
      }
      else if (newField === 'fromLoc') {
        feature.properties.fromLoc = newValue;
        feature.properties.name = newValue + ' to ' + feature.properties.toLoc;
      }
      else if (newField === 'toLoc') {
        feature.properties.toLoc = newValue;
        feature.properties.name = feature.properties.fromLoc + ' to ' + newValue;
      }
      else if (newField === 'color' || newField === 'Color') {
        const colors = getColors(newValue);
        feature.properties['color'] = colors.rgbColor;
        feature.properties['defaultColor'] = colors.rgbColor;
        feature.properties['colorHover'] = colors.rgbColorHover;
        feature.properties['colorHex'] = colors.hexColor;
        feature.properties['colorRgb'] = colors.rgbColor;
        feature.properties['colorHoverHex'] = colors.hexColorHover;
        feature.properties['colorHoverRgb'] = colors.rgbColorHover;
        feature.properties['colorInt'] = hexColorToInt(colors.hexColor);
      }
      else {
        feature.properties[newField] = newValue;
      }
    }
  }

}


export function updateGeoJsonPointFromName(geoJson, name, newCoords=null, newName=null) {

  const retGeoJson = {...geoJson};
  for (const feature of retGeoJson['features']) {
    if(feature.geometry.type === 'Point') {
      if (feature.properties.name === name) {
        if (newCoords) {
          feature.geometry.coordinates = newCoords
        }
        if (newName) {
          feature.properties.name = newName
        }
        break
      }
    }
  }
  return retGeoJson;
}

const updateGeoJsonTripFromLocationUuid = (geoJson, uuid, updateCol, updateValue) => {

  /**
   * helper function for updating a matched row on each iteration.
   * @param {*} feature 
   * @param {*} updateCol 
   * @param {*} updateValue 
   * @param {*} matchedField Whether the matched field is "from" or "to"
   */
  const updateField = (feature, updateCol, updateValue, matchedField) => {
    const newFeature = {...feature}
    let actualUpdateCol = updateCol;
    if (updateCol === 'name') {
      if (matchedField === 'from') {
        actualUpdateCol = 'fromLoc';
        newFeature['name'] = updateValue + ' to ' + newFeature.properties['toLoc']
      } else {
        actualUpdateCol = 'toLoc';
        newFeature.properties['name'] = newFeature.properties['fromLoc'] + ' to ' + updateValue
      }
    }

    if (updateCol === 'latitude') {
      if (matchedField === 'from') {
        actualUpdateCol = 'fromLat';
        newFeature.geometry.coordinates[0][1] = updateValue
      } else {
        actualUpdateCol = 'toLat';
        newFeature.geometry.coordinates[1][1] = updateValue
      }
    }
    
    if (updateCol === 'longitude') {
      if (matchedField === 'from') {
        actualUpdateCol = 'fromLng';
        newFeature.geometry.coordinates[0][0] = updateValue
      } else {
        actualUpdateCol = 'toLng';
        newFeature.geometry.coordinates[1][0] = updateValue
      }
    }  

    newFeature.properties[actualUpdateCol] = updateValue;
    return newFeature
  }

  const retGeoJson = {
    ...geoJson,
    features: geoJson['features'].map(feature => {
      if (feature.geometry.type !== 'LineString') return feature;
      let updated = feature;
      if (feature.properties.toUuid === uuid) updated = updateField(updated, updateCol, updateValue, 'to');
      if (updated.properties.fromUuid === uuid) updated = updateField(updated, updateCol, updateValue, 'from');
      return updated;
    }),
  };
  return retGeoJson
}

const applyTripUpdates = (feature, newFromCoordsArg, newToCoordsArg, newFromName, newToName, geoJson) => {
  const coords = [
    newFromCoordsArg || feature.geometry.coordinates[0],
    newToCoordsArg || feature.geometry.coordinates[1],
  ];
  const updatedFeature = {
    ...feature,
    geometry: { ...feature.geometry, coordinates: coords },
    properties: { ...feature.properties },
  };
  if (newFromName) {
    updatedFeature.properties.fromLoc = newFromName;
    updatedFeature.geometry.coordinates[0] = getCoordsFromLocationName(geoJson, newFromName);
  }
  if (newToName) {
    updatedFeature.properties.toLoc = newToName;
    updatedFeature.geometry.coordinates[1] = getCoordsFromLocationName(geoJson, newToName);
  }
  updatedFeature.properties.name = updatedFeature.properties.fromLoc + ' to ' + updatedFeature.properties.toLoc;
  return updatedFeature;
};

const buildNewTripEntry = (geoJson, fromName, toName, newFromName, newToName) => {
  const resolvedFromName = newFromName || fromName;
  const resolvedToName = newToName || toName;
  const fromCoords = getCoordsFromLocationName(geoJson, resolvedFromName);
  const toCoords = getCoordsFromLocationName(geoJson, resolvedToName);
  return createTripGeoJson({
    fromLat: fromCoords[1], fromLng: fromCoords[0],
    toLat: toCoords[1], toLng: toCoords[0],
    fromName: resolvedFromName, toName: resolvedToName,
    uuid: null, fromUuid: null, toUuid: null,
  }).geoJson;
};

export function updateGeoJsonTripFromNames(geoJson, fromName, toName,
  newFromName=null, newToName=null, newFromCoords=null, newToCoords=null) {

  const features = [...geoJson['features']];
  const matchIdx = features.findIndex(
    f => f.geometry.type === 'LineString' && f.properties.fromLoc === fromName && f.properties.toLoc === toName
  );

  if (matchIdx !== -1) {
    features[matchIdx] = applyTripUpdates(features[matchIdx], newFromCoords, newToCoords, newFromName, newToName, geoJson);
  } else {
    features.push(buildNewTripEntry(geoJson, fromName, toName, newFromName, newToName));
  }

  return { ...geoJson, features };
}

export function getCoordsFromLocationName(geoJson, locName) {
  let coords;
  for (const elem of geoJson['features']) {
    if (elem.geometry.type === 'Point') {
      if (elem.properties.name === locName) {
        coords = elem.geometry.coordinates;
        break;
      }
    }
  };
  return coords;
}

export function getFieldFromLocationName(geoJson, locName, field) {
  let val;
  for (const elem of geoJson['features']) {
    if (elem.geometry.type === 'Point') {
      if (elem.properties.name === locName) {
        val = elem.properties[field];
        break;
      }
    }
  };
  return val;
}

export function checkIfNameExists(geoJson, locName, maxIter=100) {
  const nameExists = (locName) => {
    let exists = false;
    for (const elem of geoJson['features']) {
      if (elem.properties.name === locName) {
        exists = true;
        break
      }
    }
    return exists;
  }
  let idx = 1;
  let curLocName = locName;
  while (idx < maxIter) {
    const exists = nameExists(curLocName);
    if (!exists) {
      break
    }
    curLocName = locName + ' (' + String(idx) + ')'
    idx += 1
  }
  return curLocName
}

export const initializeGeoJson = (data) => {
  const initGeoJson = {
      type: "FeatureCollection", 
      features:[],
      invalidFeatures: []
  }
  if (data) {
    // Lookup of locations by UUID so trip endpoints can be resolved by reference
    // (canonical trips carry fromLocationId/toLocationId, not coordinates).
    const locationsByUuid = new Map((data['locations'] || []).map((l) => [l['UUID'], l]));

    for (const locRow of data['locations']) {
        const loc = locRow['locationName'];
        const lat = locRow['latitude'];
        const lng = locRow['longitude'];
        const uuid = locRow['UUID'];
        const color = locRow['color'] || locRow['Color'];

        const geoJsonPoint = createPointGeoJson({
          locationName: loc,
          latitude: lat,
          longitude: lng,
          uuid: uuid,
          color: color,
          dataRow: locRow
        });
        initGeoJson['features'].push(geoJsonPoint);
    }
    for (const tripRow of data['trips']) {
        const fromUuid = tripRow['fromLocationId'];
        const toUuid = tripRow['toLocationId'];
        const fromLoc = locationsByUuid.get(fromUuid);
        const toLoc = locationsByUuid.get(toUuid);

        // Resolve endpoints from the referenced locations; fall back to any
        // denormalized values on the trip row (legacy scenarios).
        const fromLat = fromLoc ? fromLoc['latitude'] : tripRow['fromLatitude'];
        const fromLng = fromLoc ? fromLoc['longitude'] : tripRow['fromLongitude'];
        const toLat = toLoc ? toLoc['latitude'] : tripRow['toLatitude'];
        const toLng = toLoc ? toLoc['longitude'] : tripRow['toLongitude'];
        const fromName = tripRow['fromLocationName'] || (fromLoc && fromLoc['locationName']);
        const toName = tripRow['toLocationName'] || (toLoc && toLoc['locationName']);
        const uuid = tripRow['UUID'];
        const color = tripRow['color'] || tripRow['Color'];
        const geoJsonTrip = createTripGeoJson({
            fromLat: fromLat,
            fromLng: fromLng,
            toLat: toLat,
            toLng: toLng,
            fromName: fromName,
            toName: toName,
            uuid: uuid,
            fromUuid: fromUuid,
            toUuid: toUuid,
            color: color,
            type: 'trip'});

        initGeoJson['features'].push(geoJsonTrip.geoJson);
    }
  }

  return initGeoJson;
}


export const addRowGeoJsonHelper = (state, uuid, newRow, tableName) => {
  const newState = {...state};

  if (!uuid) {
      throw new Error('No uuid recieved in addRow geoJsonReducer');
  }
  if (tableName === 'locations') {
    const lat = newRow['latitude'];
    const lng = newRow['longitude'];
    let locName = newRow['locationName'];
    locName = checkIfNameExists(newState, locName);
    const color = newRow['Color']
    const pointGeoJson = createPointGeoJson({
      locationName: locName,
      latitude: lat,
      longitude: lng,
      uuid: uuid,
      color: color,
      dataRow: newRow});
    if (!pointGeoJson) {
        return newState
    }
    newState['features'].push(pointGeoJson);
  }

  if (tableName === 'trips') {
      const fromName = newRow['fromLocationName'];
      let fromLng = newRow['fromLongitude'];
      let fromLat = newRow['fromLatitude'];

      // Only look coordinates up when they're genuinely absent — 0 is a real
      // coordinate, especially on an x-y grid.
      if (!isCoord(fromLng) || !isCoord(fromLat)) {
          const fromCoords = getCoordsFromLocationName(newState, fromName);
          if (fromCoords) {
              fromLat = fromCoords[1];
              fromLng = fromCoords[0];
          }
      }

      const toName = newRow['toLocationName'];
      let toLng = newRow['toLongitude'];
      let toLat = newRow['toLatitude'];
      
      if (!isCoord(toLat) || !isCoord(toLng)) {
          const toCoords = getCoordsFromLocationName(newState, toName);
          if (toCoords) {
              toLat = toCoords[1];
              toLng = toCoords[0];
          }
      }
      
      const fromUuid = newRow['fromLocationId']
      const toUuid = newRow['toLocationId']

      const tripResponse = createTripGeoJson({
          fromLat: fromLat,
          fromLng: fromLng,
          toLat: toLat,
          toLng: toLng,
          fromName: fromName,
          toName: toName,
          uuid: uuid,
          fromUuid: fromUuid,
          toUuid: toUuid
      });
      const tripGeoJson = tripResponse['geoJson'];
      newState['features'].push(tripGeoJson)
  }
  return newState;
}


export const deleteRowGeoJsonHelper = (state, uuid, tableName) => {
  if (tableName === 'locations') {
      state['features'] = state['features'].filter(function(item) { 
          return (item.properties.uuid !== uuid);  
       });
       state['features'] = state['features'].filter(function(item) { 
          return (item.properties.toUuid !== uuid);  
       });
       state['features'] = state['features'].filter(function(item) { 
          return (item.properties.fromUuid !== uuid);  
       });
       state['invalidFeatures'] = state['invalidFeatures'].filter(function(item) { 
          return (item.properties.uuid !== uuid);
       });
       state['invalidFeatures'] = state['invalidFeatures'].filter(function(item) { 
          return (item.properties.toUuid !== uuid);
       });
       state['invalidFeatures'] = state['invalidFeatures'].filter(function(item) { 
          return (item.properties.fromUuid !== uuid);  
       });
      }

  if (tableName === 'trips') {
    state['features'] = state['features'].filter(function(item) { 
          return (item.properties.uuid !== uuid);  
      });
  }
  
  if (tableName.search('results') >= 0) {
    state['features'] = state['features'].filter(function(item) { 
          return (item.properties.uuid !== uuid);  
      });
  }
  return state
}

export const clearGeoJsonResults = (state) => {
  state['features'] = state['features'].filter(function(item) { 
    return  !(item.properties.type === 'routeLeg' || item.properties.type === 'routePoint' ||
            item.properties.isDrop || item.properties.isResult === true
    );
  })
  return state
}

export const addGeoJsonTripHelper = (state, uuid, toUuid, fromUuid, newTrip) => {

  if (!uuid) {
      throw new Error('No uuid recieved in addRow geoJsonReducer');
  }
  if (!toUuid) {
      throw new Error('No toUuid recieved in addRow geoJsonReducer');
  }
  if (!fromUuid) {
      throw new Error('No fromUuid recieved in addRow geoJsonReducer');
  }


  const toLoc = state['features'].filter(function(item) {
      return ((item.geometry.coordinates[0] === newTrip.geometry.coordinates[1][0]) && (item.geometry.coordinates[1] === newTrip.geometry.coordinates[1][1]))
  });

  const fromLoc = state['features'].filter(function(item) {
      return ((item.geometry.coordinates[0] === newTrip.geometry.coordinates[0][0]) && (item.geometry.coordinates[1] === newTrip.geometry.coordinates[0][1]))
  });


  const coords = newTrip.geometry.coordinates;
  const fromLat = coords[0][1];
  const fromLng = coords[0][0];
  const toLat = coords[1][1];
  const toLng = coords[1][0]
  const myNewTrip = createTripGeoJson({
      fromLat: fromLat, 
      fromLng: fromLng,
      toLat: toLat, 
      toLng: toLng,
      fromName: fromLoc[0].properties.name, 
      toName: toLoc[0].properties.name,
      uuid: uuid,
      fromUuid: fromUuid,
      toUuid: toUuid
  })
  state['features'].push(myNewTrip.geoJson);
  return state;
}

export const addGeoJsonResults = (state, vehiclePaths, drops, locations=null) => {
  if (state['features'].length > 0) {
    state['features'] = state['features'].filter(function(item) {
        return !(item.properties.type === 'routeLeg');
    });
  }

  // Resolves coordinates for result rows that arrive without them (cartesian
  // models submit null lat/lng; the solver only reads the cost matrix).
  const locationsById = buildLocationLookup(locations);

  const routeGeoJson = createRoutesGeoJson({vehiclePaths: vehiclePaths, showRetours:true, locationsById });

  state['features'].push.apply(state['features'], routeGeoJson)

  if (drops) {
    const dropGeoJson = createDropsGeoJson({drops: drops, locationsById})
    state['features'].push.apply(state['features'], dropGeoJson)
  }
  return state
}

export const updateTableHelper = (state, tableName, columnName, uuid, newValue) => {

  if (!uuid) {
        const err_msg = 'no UUID provided in the dispatch payload for updateTable.';
        throw err_msg;
      }

      if (!(tableName === 'locations' || tableName === 'trips')) {
          return
      }

      if (tableName === 'locations') {
          if (columnName === 'locationName') {
              updateGeoJsonFromUUID(state, uuid, 'name', newValue);
              state = updateGeoJsonTripFromLocationUuid(state, uuid, 'name', newValue)
          }
          if (columnName === 'latitude') {
              updateGeoJsonFromUUID(state, uuid, 'lat', parseFloat(newValue));
              state = updateGeoJsonTripFromLocationUuid(state, uuid, 'latitude', newValue)
          }
          if (columnName === 'longitude') {
              updateGeoJsonFromUUID(state, uuid, 'lng', parseFloat(newValue));
              state = updateGeoJsonTripFromLocationUuid(state, uuid, 'longitude', newValue)
          }
          else{
            updateGeoJsonFromUUID(state, uuid, columnName, newValue); 
          }
          
      }
      if (tableName === 'trips') {
          if (columnName === 'fromLocationName') {
              updateGeoJsonFromUUID(state, uuid, 'fromLoc', newValue);
              const newCoords = getCoordsFromLocationName(state, newValue);
              updateGeoJsonFromUUID(state, uuid, 'fromCoords', newCoords);
          }
          if (columnName === 'toLocationName') {
              updateGeoJsonFromUUID(state, uuid, 'toLoc', newValue);
              const newCoords = getCoordsFromLocationName(state, newValue);
              updateGeoJsonFromUUID(state, uuid, 'toCoords', newCoords);
              const toUuid = getFieldFromLocationName(state, newValue, 'uuid')
              updateGeoJsonFromUUID(state, uuid, 'toUuid', toUuid);
          }                    
      }
    return state;
}


export const onLocationDataChangeGeoJsonHelper = (state, action) => {
  const updatedState = action.payload.newState;
  if (updatedState.cellEdit) {
      let newState = {...state}
      const oldState = action.payload.oldState
      const tableName = action.payload.tableName;
      const uuid = updatedState.cellEdit.rowId;
      const newValueField = updatedState.cellEdit.dataField;
      const newValue = updatedState.cellEdit.newValue;

      if (tableName === 'locations') {
          const locationItem = oldState[tableName].find(x => x['UUID'] === uuid);
          const locName = locationItem['locationName'];
          // Both branches previously read `longitude` and tested for
          // 'longitude', so editing latitude (Y) never moved the point and
          // editing longitude (X) also overwrote latitude with the X value.
          let lat = newValueField === 'latitude' ? newValue : locationItem['latitude'];
          let lng = newValueField === 'longitude' ? newValue : locationItem['longitude'];
          let newName = null;
          if (newValueField === 'locationName') {
              newName = newValue
          }
          const newCoords = [parseFloat(lng), parseFloat(lat)]
          newState = updateGeoJsonPointFromName(newState, locName, newCoords, newName);
          state = newState;
      }

      if (tableName === 'trips') {
          const tripItem = oldState[tableName].find(x => x['UUID'] === uuid);
          const fromName = tripItem['fromLocationName'];
          const toName = tripItem['toLocationName'];
          let newFromName = null;
          if (newValueField === 'fromLocationName') {
              newFromName = newValue;
          }
          let newToName = null;
          if (newValueField === 'toLocationName') {
              newToName = newValue;
          }
          let newFromLat = tripItem['fromLatitude']
          if (newValueField === 'fromLatitude') {
              newFromLat = newValue;
          }
          let newFromLng = tripItem['fromLongitude']
          if (newValueField === 'fromLongitude') {
              newFromLng = newValue;
          }    
          let newToLat = tripItem['toLatitude']
          if (newValueField === 'toLatitude') {
              newToLat = newValue;
          }    
          let newToLng = tripItem['toLongitude']
          if (newValueField === 'toLongitude') {
              newToLng = newValue;
          }    
          const newFromCoords = [newFromLng, newFromLat];
          const newToCoords = [newToLng, newToLat]

          newState = updateGeoJsonTripFromNames(newState, fromName, toName, newFromName, 
              newToName, newFromCoords, newToCoords);
          state = newState
      }
  }
  return state;
}
